import { useState, useEffect } from 'react';
import { adminApi } from '../services/api';
import toast from 'react-hot-toast';
import { Dialog } from '@headlessui/react';
import { format, formatDistanceToNow } from 'date-fns';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  BugAntIcon,
  WrenchIcon,
  ChatBubbleLeftRightIcon,
  ServerStackIcon,
  UserIcon,
  EyeIcon,
  SparklesIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CodeBracketIcon,
  CommandLineIcon
} from '@heroicons/react/24/outline';

const ISSUE_TYPES = [
  { value: 'error', label: 'Error', icon: BugAntIcon },
  { value: 'missing_tool', label: 'Missing Tool', icon: WrenchIcon },
  { value: 'missing_template', label: 'Missing Template', icon: ServerStackIcon },
  { value: 'user_reported', label: 'User Reported', icon: UserIcon },
  { value: 'ai_limitation', label: 'AI Limitation', icon: ChatBubbleLeftRightIcon },
  { value: 'import_failure', label: 'Import Failure', icon: ServerStackIcon },
  { value: 'import_partial_failure', label: 'Partial Import Failure', icon: ExclamationTriangleIcon },
  { value: 'view_deployment_failure', label: 'View Deployment Failure', icon: ServerStackIcon },
  { value: 'system_error', label: 'System Error', icon: ExclamationTriangleIcon }
];

const SEVERITIES = [
  { value: 'critical', label: 'Critical', color: 'badge-danger' },
  { value: 'high', label: 'High', color: 'badge-warning' },
  { value: 'medium', label: 'Medium', color: 'badge-info' },
  { value: 'low', label: 'Low', color: 'badge-neutral' }
];

const STATUSES = [
  { value: 'open', label: 'Open', color: 'badge-danger' },
  { value: 'in_progress', label: 'In Progress', color: 'badge-warning' },
  { value: 'resolved', label: 'Resolved', color: 'badge-success' },
  { value: 'wont_fix', label: "Won't Fix", color: 'badge-neutral' }
];

function CreateIssueModal({ isOpen, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    type: 'user_reported',
    severity: 'medium',
    title: '',
    description: '',
    source: 'user_report'
  });
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await adminApi.createIssue(formData);
      toast.success('Issue created successfully!');
      onSuccess();
      onClose();
      setFormData({ type: 'user_reported', severity: 'medium', title: '', description: '', source: 'user_report' });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create issue');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="card w-full max-w-lg">
          <div className="flex items-center justify-between mb-6">
            <Dialog.Title className="text-lg font-semibold text-surface-100">
              Create New Issue
            </Dialog.Title>
            <button onClick={onClose} className="text-surface-400 hover:text-surface-100">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))}
                className="input"
                placeholder="Brief description of the issue"
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-1">
                  Type
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData(p => ({ ...p, type: e.target.value }))}
                  className="input"
                >
                  {ISSUE_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-1">
                  Severity
                </label>
                <select
                  value={formData.severity}
                  onChange={(e) => setFormData(p => ({ ...p, severity: e.target.value }))}
                  className="input"
                >
                  {SEVERITIES.map(sev => (
                    <option key={sev.value} value={sev.value}>{sev.label}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                className="input h-32 resize-none"
                placeholder="Detailed description of the issue..."
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <button type="button" onClick={onClose} className="btn-secondary flex-1">
                Cancel
              </button>
              <button type="submit" disabled={loading} className="btn-primary flex-1">
                {loading ? 'Creating...' : 'Create Issue'}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

function IssueDetailModal({ issue, isOpen, onClose, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [resolution, setResolution] = useState({ notes: '', action: '' });
  const [showContext, setShowContext] = useState(false);
  const [showConversation, setShowConversation] = useState(false);
  const [localIssue, setLocalIssue] = useState(issue);
  
  // Update local issue when prop changes
  useEffect(() => {
    setLocalIssue(issue);
  }, [issue]);
  
  if (!localIssue) return null;
  
  const handleResolve = async () => {
    setLoading(true);
    try {
      await adminApi.updateIssue(localIssue._id, {
        status: 'resolved',
        resolution
      });
      toast.success('Issue resolved!');
      onUpdate();
      onClose();
    } catch (error) {
      toast.error('Failed to update issue');
    } finally {
      setLoading(false);
    }
  };
  
  const handleStatusChange = async (status) => {
    setLoading(true);
    try {
      await adminApi.updateIssue(localIssue._id, { status });
      toast.success('Status updated!');
      onUpdate();
      onClose();
    } catch (error) {
      toast.error('Failed to update issue');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      const response = await adminApi.analyzeIssue(localIssue._id);
      toast.success('AI analysis complete!');
      // Update local issue with analysis
      setLocalIssue(prev => ({
        ...prev,
        aiSummary: response.data.analysis,
        severity: response.data.updatedSeverity,
        tags: response.data.tags
      }));
      onUpdate(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to analyze issue');
    } finally {
      setAnalyzing(false);
    }
  };
  
  const typeInfo = ISSUE_TYPES.find(t => t.value === localIssue.type);
  const TypeIcon = typeInfo?.icon || ExclamationTriangleIcon;
  
  const hasContext = localIssue.context?.recentActivity?.length > 0 || localIssue.context?.conversationHistory?.length > 0;
  const hasConversation = localIssue.context?.conversationHistory?.length > 0;
  
  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="card w-full max-w-3xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-surface-700 rounded-lg">
                <TypeIcon className="w-6 h-6 text-surface-300" />
              </div>
              <div>
                <Dialog.Title className="text-lg font-semibold text-surface-100">
                  {localIssue.title}
                </Dialog.Title>
                <p className="text-sm text-surface-400">
                  Created {formatDistanceToNow(new Date(localIssue.createdAt))} ago
                </p>
              </div>
            </div>
            <button onClick={onClose} className="text-surface-400 hover:text-surface-100">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          
          {/* Badges */}
          <div className="flex flex-wrap gap-2 mb-6">
            <span className={STATUSES.find(s => s.value === localIssue.status)?.color}>
              {STATUSES.find(s => s.value === localIssue.status)?.label}
            </span>
            <span className={SEVERITIES.find(s => s.value === localIssue.severity)?.color}>
              {localIssue.severity}
            </span>
            <span className="badge-neutral">
              {typeInfo?.label}
            </span>
            {localIssue.context?.feature && (
              <span className="badge-info">
                {localIssue.context.feature}
              </span>
            )}
            {localIssue.occurrenceCount > 1 && (
              <span className="badge-warning">
                {localIssue.occurrenceCount} occurrences
              </span>
            )}
            {localIssue.tags?.map(tag => (
              <span key={tag} className="badge-neutral text-xs">{tag}</span>
            ))}
          </div>
          
          {/* AI Analysis Button & Summary */}
          <div className="mb-6">
            {localIssue.aiSummary?.summary ? (
              <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/20 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <SparklesIcon className="w-5 h-5 text-purple-400" />
                  <h4 className="text-sm font-medium text-purple-300">AI Analysis</h4>
                  <span className="text-xs text-surface-500 ml-auto">
                    Analyzed {localIssue.aiSummary.analyzedAt && formatDistanceToNow(new Date(localIssue.aiSummary.analyzedAt))} ago
                  </span>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-surface-400 uppercase tracking-wide mb-1">Summary</p>
                    <p className="text-sm text-surface-200">{localIssue.aiSummary.summary}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-surface-400 uppercase tracking-wide mb-1">What User Was Trying</p>
                      <p className="text-sm text-surface-300">{localIssue.aiSummary.whatUserWasTrying}</p>
                    </div>
                    <div>
                      <p className="text-xs text-surface-400 uppercase tracking-wide mb-1">What Went Wrong</p>
                      <p className="text-sm text-surface-300">{localIssue.aiSummary.whatWentWrong}</p>
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-xs text-surface-400 uppercase tracking-wide mb-1">Likely Cause</p>
                    <p className="text-sm text-amber-300">{localIssue.aiSummary.likelyCause}</p>
                  </div>
                  
                  <div>
                    <p className="text-xs text-surface-400 uppercase tracking-wide mb-1">Suggested Fix</p>
                    <p className="text-sm text-emerald-300">{localIssue.aiSummary.suggestedFix}</p>
                  </div>
                </div>
                
                <button
                  onClick={handleAnalyze}
                  disabled={analyzing}
                  className="mt-3 text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1"
                >
                  <ArrowPathIcon className={`w-3 h-3 ${analyzing ? 'animate-spin' : ''}`} />
                  Re-analyze
                </button>
              </div>
            ) : (
              <button
                onClick={handleAnalyze}
                disabled={analyzing}
                className="w-full bg-gradient-to-r from-purple-600/20 to-blue-600/20 hover:from-purple-600/30 hover:to-blue-600/30 border border-purple-500/30 rounded-lg p-4 flex items-center justify-center gap-2 transition-all"
              >
                {analyzing ? (
                  <>
                    <ArrowPathIcon className="w-5 h-5 text-purple-400 animate-spin" />
                    <span className="text-purple-300">Analyzing with AI...</span>
                  </>
                ) : (
                  <>
                    <SparklesIcon className="w-5 h-5 text-purple-400" />
                    <span className="text-purple-300">Analyze with AI</span>
                  </>
                )}
              </button>
            )}
          </div>
          
          {/* Description */}
          {localIssue.description && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-surface-300 mb-2">Description</h4>
              <p className="text-surface-400 bg-surface-700/50 rounded-lg p-4">
                {localIssue.description}
              </p>
            </div>
          )}
          
          {/* Error details */}
          {localIssue.errorDetails?.message && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-surface-300 mb-2">Error Details</h4>
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                <p className="text-red-400 font-mono text-sm">{localIssue.errorDetails.message}</p>
                {localIssue.errorDetails.stack && (
                  <pre className="text-red-400/70 text-xs mt-2 overflow-x-auto max-h-32">
                    {localIssue.errorDetails.stack}
                  </pre>
                )}
              </div>
            </div>
          )}
          
          {/* SQL Query (if present) */}
          {localIssue.context?.sqlQuery && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-surface-300 mb-2 flex items-center gap-2">
                <CodeBracketIcon className="w-4 h-4" />
                SQL Query
              </h4>
              <pre className="bg-surface-800 border border-surface-700 rounded-lg p-4 text-xs text-surface-300 overflow-x-auto max-h-32">
                {localIssue.context.sqlQuery}
              </pre>
            </div>
          )}
          
          {/* Context Timeline */}
          {hasContext && (
            <div className="mb-6">
              <button
                onClick={() => setShowContext(!showContext)}
                className="flex items-center gap-2 text-sm font-medium text-surface-300 mb-2 hover:text-surface-100"
              >
                <ClockIcon className="w-4 h-4" />
                Recent Activity ({localIssue.context?.recentActivity?.length || 0} actions)
                {showContext ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
              </button>
              
              {showContext && (
                <div className="bg-surface-700/50 rounded-lg p-4 space-y-2 max-h-48 overflow-y-auto">
                  {localIssue.context?.recentActivity?.map((activity, idx) => (
                    <div key={idx} className="flex items-center gap-3 text-sm">
                      <span className="text-xs text-surface-500 w-20">
                        {activity.createdAt && format(new Date(activity.createdAt), 'HH:mm:ss')}
                      </span>
                      <span className="badge-neutral text-xs">{activity.resourceType}</span>
                      <span className="text-surface-300">{activity.action?.replace('.', ' → ')}</span>
                    </div>
                  ))}
                  {(!localIssue.context?.recentActivity || localIssue.context.recentActivity.length === 0) && (
                    <p className="text-surface-500 text-sm">No recent activity captured</p>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Conversation History */}
          {hasConversation && (
            <div className="mb-6">
              <button
                onClick={() => setShowConversation(!showConversation)}
                className="flex items-center gap-2 text-sm font-medium text-surface-300 mb-2 hover:text-surface-100"
              >
                <ChatBubbleLeftRightIcon className="w-4 h-4" />
                Conversation History ({localIssue.context?.conversationHistory?.length || 0} messages)
                {showConversation ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
              </button>
              
              {showConversation && (
                <div className="bg-surface-700/50 rounded-lg p-4 space-y-3 max-h-64 overflow-y-auto">
                  {localIssue.context?.conversationHistory?.map((msg, idx) => (
                    <div key={idx} className={`text-sm ${msg.role === 'user' ? 'pl-0' : 'pl-4'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-medium ${msg.role === 'user' ? 'text-blue-400' : 'text-purple-400'}`}>
                          {msg.role === 'user' ? 'User' : 'AI'}
                        </span>
                        {msg.hadToolCalls && (
                          <CommandLineIcon className="w-3 h-3 text-amber-400" title="Used tools" />
                        )}
                        <span className="text-xs text-surface-500">
                          {msg.createdAt && format(new Date(msg.createdAt), 'HH:mm:ss')}
                        </span>
                      </div>
                      <p className="text-surface-300 text-xs">{msg.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* AI Context */}
          {localIssue.aiContext && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-surface-300 mb-2">AI Context</h4>
              <div className="bg-surface-700/50 rounded-lg p-4 space-y-2">
                {localIssue.aiContext.provider && (
                  <p className="text-sm"><span className="text-surface-400">Provider:</span> <span className="text-surface-200">{localIssue.aiContext.provider}</span></p>
                )}
                {localIssue.aiContext.model && (
                  <p className="text-sm"><span className="text-surface-400">Model:</span> <span className="text-surface-200">{localIssue.aiContext.model}</span></p>
                )}
                {localIssue.aiContext.requestedTool && (
                  <p className="text-sm"><span className="text-surface-400">Requested Tool:</span> <span className="text-primary-400">{localIssue.aiContext.requestedTool}</span></p>
                )}
                {localIssue.aiContext.userQuery && (
                  <div>
                    <p className="text-sm text-surface-400 mb-1">User Query:</p>
                    <p className="text-sm text-surface-200 italic">"{localIssue.aiContext.userQuery}"</p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Import Error Details (for partial import failures) */}
          {localIssue.type === 'import_partial_failure' && localIssue.metadata && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-surface-300 mb-2">Import Error Details</h4>
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 space-y-4">
                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-surface-100">{localIssue.metadata.totalRows?.toLocaleString() || 0}</p>
                    <p className="text-xs text-surface-400">Total Rows</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-emerald-400">{localIssue.metadata.successfulRows?.toLocaleString() || 0}</p>
                    <p className="text-xs text-surface-400">Successful</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-400">{localIssue.metadata.failedRowCount?.toLocaleString() || 0}</p>
                    <p className="text-xs text-surface-400">Failed</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-amber-400">{localIssue.metadata.failureRate || '0%'}</p>
                    <p className="text-xs text-surface-400">Failure Rate</p>
                  </div>
                </div>
                
                {/* Error Types Breakdown */}
                {localIssue.metadata.errorsByType && Object.keys(localIssue.metadata.errorsByType).length > 0 && (
                  <div>
                    <p className="text-xs text-surface-400 uppercase tracking-wide mb-2">Error Types</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(localIssue.metadata.errorsByType).map(([type, count]) => (
                        <span key={type} className="badge-warning text-xs">
                          {type}: {count}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Sample Failed Rows */}
                {localIssue.metadata.sampleFailedRows && localIssue.metadata.sampleFailedRows.length > 0 && (
                  <div>
                    <p className="text-xs text-surface-400 uppercase tracking-wide mb-2">
                      Sample Failed Rows ({localIssue.metadata.sampleFailedRows.length} shown)
                    </p>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {localIssue.metadata.sampleFailedRows.slice(0, 5).map((row, idx) => (
                        <div key={idx} className="bg-surface-800 rounded p-2 text-xs">
                          <span className="text-amber-400 font-medium">Row {row.rowIndex}:</span>
                          {row.errors?.map((err, errIdx) => (
                            <span key={errIdx} className="text-red-400 ml-2">
                              {err.reason} - {err.message?.substring(0, 100)}
                            </span>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Metadata */}
                <div className="grid grid-cols-2 gap-4 text-xs">
                  <div>
                    <span className="text-surface-400">Data Source Type:</span>
                    <span className="text-surface-200 ml-2">{localIssue.metadata.dataSourceType}</span>
                  </div>
                  <div>
                    <span className="text-surface-400">Import Mode:</span>
                    <span className="text-surface-200 ml-2">{localIssue.metadata.importMode}</span>
                  </div>
                  {localIssue.metadata.totalRetries > 0 && (
                    <div>
                      <span className="text-surface-400">Total Retries:</span>
                      <span className="text-amber-400 ml-2">{localIssue.metadata.totalRetries}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Related entities */}
          <div className="mb-6 grid grid-cols-2 gap-4">
            {localIssue.userId && (
              <div className="bg-surface-700/50 rounded-lg p-3">
                <p className="text-xs text-surface-400 mb-1">Affected User</p>
                <p className="text-sm text-surface-200">
                  {localIssue.userId.firstName} {localIssue.userId.lastName}
                </p>
              </div>
            )}
            {localIssue.companyId && (
              <div className="bg-surface-700/50 rounded-lg p-3">
                <p className="text-xs text-surface-400 mb-1">Company</p>
                <p className="text-sm text-surface-200">{localIssue.companyId.name}</p>
              </div>
            )}
          </div>
          
          {/* Resolution section */}
          {localIssue.status !== 'resolved' && localIssue.status !== 'wont_fix' && (
            <div className="border-t border-surface-700 pt-6">
              <h4 className="text-sm font-medium text-surface-300 mb-3">Resolve Issue</h4>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-surface-400 mb-1">Action Taken</label>
                  <select
                    value={resolution.action}
                    onChange={(e) => setResolution(p => ({ ...p, action: e.target.value }))}
                    className="input"
                  >
                    <option value="">Select action...</option>
                    <option value="fixed_bug">Fixed Bug</option>
                    <option value="added_tool">Added Tool</option>
                    <option value="added_template">Added Template</option>
                    <option value="documented">Documented</option>
                    <option value="configuration_change">Configuration Change</option>
                    <option value="user_education">User Education</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs text-surface-400 mb-1">Notes</label>
                  <textarea
                    value={resolution.notes}
                    onChange={(e) => setResolution(p => ({ ...p, notes: e.target.value }))}
                    className="input h-24 resize-none"
                    placeholder="Describe the resolution..."
                  />
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => handleStatusChange('in_progress')}
                    disabled={loading || localIssue.status === 'in_progress'}
                    className="btn-secondary flex-1"
                  >
                    Mark In Progress
                  </button>
                  <button
                    onClick={() => handleStatusChange('wont_fix')}
                    disabled={loading}
                    className="btn-ghost flex-1"
                  >
                    Won't Fix
                  </button>
                  <button
                    onClick={handleResolve}
                    disabled={loading || !resolution.action}
                    className="btn-success flex-1"
                  >
                    {loading ? 'Saving...' : 'Resolve'}
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Resolved info */}
          {localIssue.status === 'resolved' && localIssue.resolution && (
            <div className="border-t border-surface-700 pt-6">
              <h4 className="text-sm font-medium text-surface-300 mb-3 flex items-center gap-2">
                <CheckCircleIcon className="w-5 h-5 text-emerald-400" />
                Resolution
              </h4>
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4">
                <p className="text-sm text-emerald-400 mb-2">
                  <span className="font-medium">Action:</span> {localIssue.resolution.action?.replace('_', ' ')}
                </p>
                {localIssue.resolution.notes && (
                  <p className="text-sm text-emerald-300">{localIssue.resolution.notes}</p>
                )}
                <p className="text-xs text-emerald-400/70 mt-2">
                  Resolved on {format(new Date(localIssue.resolvedAt), 'PPP')}
                  {localIssue.resolvedBy && ` by ${localIssue.resolvedBy.firstName} ${localIssue.resolvedBy.lastName}`}
                </p>
              </div>
            </div>
          )}
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

export default function Issues() {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [filters, setFilters] = useState({ status: '', type: '', severity: '' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  
  useEffect(() => {
    loadIssues();
  }, [pagination.page, filters]);
  
  const loadIssues = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getIssues({
        page: pagination.page,
        limit: 20,
        ...filters
      });
      setIssues(response.data.issues);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error('Failed to load issues');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Issue Tracker</h1>
          <p className="text-surface-400 mt-1">Track and resolve platform issues</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn-primary">
          <PlusIcon className="w-5 h-5" />
          Report Issue
        </button>
      </div>
      
      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <select
            value={filters.status}
            onChange={(e) => setFilters(p => ({ ...p, status: e.target.value }))}
            className="input"
          >
            <option value="">All Status</option>
            {STATUSES.map(status => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>
          
          <select
            value={filters.type}
            onChange={(e) => setFilters(p => ({ ...p, type: e.target.value }))}
            className="input"
          >
            <option value="">All Types</option>
            {ISSUE_TYPES.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
          
          <select
            value={filters.severity}
            onChange={(e) => setFilters(p => ({ ...p, severity: e.target.value }))}
            className="input"
          >
            <option value="">All Severities</option>
            {SEVERITIES.map(sev => (
              <option key={sev.value} value={sev.value}>{sev.label}</option>
            ))}
          </select>
          
          <button
            onClick={() => setFilters({ status: '', type: '', severity: '' })}
            className="btn-ghost"
          >
            Clear Filters
          </button>
        </div>
      </div>
      
      {/* Issues Table */}
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Issue</th>
              <th>Type</th>
              <th>Severity</th>
              <th>Status</th>
              <th>Occurrences</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="text-center py-8">
                  <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" />
                </td>
              </tr>
            ) : issues.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center py-8 text-surface-400">
                  No issues found
                </td>
              </tr>
            ) : (
              issues.map(issue => {
                const typeInfo = ISSUE_TYPES.find(t => t.value === issue.type);
                const TypeIcon = typeInfo?.icon || ExclamationTriangleIcon;
                
                return (
                  <tr key={issue._id}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-surface-700 rounded-lg">
                          <TypeIcon className="w-4 h-4 text-surface-400" />
                        </div>
                        <div>
                          <p className="font-medium text-surface-100 max-w-xs truncate">
                            {issue.title}
                          </p>
                          {issue.companyId && (
                            <p className="text-xs text-surface-400">{issue.companyId.name}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="badge-neutral">{typeInfo?.label}</span>
                    </td>
                    <td>
                      <span className={SEVERITIES.find(s => s.value === issue.severity)?.color}>
                        {issue.severity}
                      </span>
                    </td>
                    <td>
                      <span className={STATUSES.find(s => s.value === issue.status)?.color}>
                        {STATUSES.find(s => s.value === issue.status)?.label}
                      </span>
                    </td>
                    <td className="text-surface-300">
                      {issue.occurrenceCount}
                    </td>
                    <td className="text-surface-400 text-sm">
                      {formatDistanceToNow(new Date(issue.createdAt))} ago
                    </td>
                    <td>
                      <button
                        onClick={() => setSelectedIssue(issue)}
                        className="btn-ghost btn-sm"
                      >
                        <EyeIcon className="w-4 h-4" />
                        View
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      
      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-surface-400 text-sm">
            Showing {issues.length} of {pagination.total} issues
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
              disabled={pagination.page === 1}
              className="btn-secondary btn-sm"
            >
              Previous
            </button>
            <button
              onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
              disabled={pagination.page === pagination.pages}
              className="btn-secondary btn-sm"
            >
              Next
            </button>
          </div>
        </div>
      )}
      
      <CreateIssueModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={loadIssues}
      />
      
      <IssueDetailModal
        issue={selectedIssue}
        isOpen={!!selectedIssue}
        onClose={() => setSelectedIssue(null)}
        onUpdate={loadIssues}
      />
    </div>
  );
}

